import type {
  DeliveryExperienceCallPayloadsDto,
  DeliveryExperienceDeliveryEstimateFailureDto,
  DeliveryExperienceDeliveryEstimateSummaryDto,
  DeliveryExperienceGetDeliveryEstimatesParams,
  DeliveryExperiencePromiseExplainerResponse,
} from '@shipium-corp/shipium-ts-types';
import qs from 'qs';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import { toDeliveryEstimatesError } from './errors';

const PROMISE_EXPLAINER_BASE = '/delivery-experience-proxy/api/v1/promiseexplainer';
const DELIVERY_ESTIMATES_BASE = `${PROMISE_EXPLAINER_BASE}/deliveryestimates`;
const DELIVERY_ESTIMATE_FAILURES_BASE = `${PROMISE_EXPLAINER_BASE}/deliveryestimatefailures`;

// Why a sampled row produced no estimate. The generated contract types the reason as a plain string;
// this names the backend DeliveryEstimateFailureReason values the UI labels, and stays open-ended so
// a reason added server-side still renders rather than breaking the row.
export type DeliveryEstimateFailureReason = 'VALIDATION_FAILURE' | 'NO_ESTIMATE' | 'INTERNAL_ERROR' | (string & {});

// Local names for the generated contract types. Every field on the generated row is optional because
// the published schema declares no required list, so consumers must treat any field as absent. A
// sampled row that produced no estimate (noEstimate true) has a null deliveryEstimateId and is
// identified by deliveryEstimateFailureId instead.
export type DeliveryEstimateSummary = DeliveryExperienceDeliveryEstimateSummaryDto;
// `page` is the 1-based page number the sampled (rollup) path navigates by; `destinationLocation`,
// `shipOption` and `origin` are its array filters, which the backend binds from repeated query params.
export type DeliveryEstimateQueryParams = DeliveryExperienceGetDeliveryEstimatesParams;
export type PromiseExplanation = DeliveryExperiencePromiseExplainerResponse;
// The failure drill-in (GET /deliveryestimatefailures/{id}): one failed delivery estimate call.
export type DeliveryEstimateFailure = DeliveryExperienceDeliveryEstimateFailureDto;
// The recorded request/response bodies of one delivery estimate call (GET .../{id}/payloads).
export type CallPayloads = DeliveryExperienceCallPayloadsDto;

// Not generated: the page/meta values below arrive as response headers, which a body schema cannot
// describe. Which set is populated depends on the pagination model the server used:
//   - Unsampled (base-table) path: keyset cursors via nextAnchor/prevAnchor (X-Page-Next/Previous).
//   - Sampled (rollup) path: OFFSET page numbers via pageNumber/totalPages plus the exact
//     distinct-signature totalCount (X-Page-Number/X-Total-Pages/X-Total-Count). It sets no anchors.
// `sampled` (X-Sampled) is the discriminator the UI reads to pick which set applies.
export interface DeliveryEstimatePage {
  rows: DeliveryEstimateSummary[];
  nextAnchor?: string;
  prevAnchor?: string;
  pageNumber?: number;
  totalPages?: number;
  totalCount?: number;
  sampled: boolean;
  asOfDateTime?: string;
}

function toOptionalNumber(headerValue: string | undefined): number | undefined {
  if (headerValue === undefined) return undefined;
  const parsed = Number(headerValue);
  return Number.isNaN(parsed) ? undefined : parsed;
}

// Axios omits `undefined` params from the outgoing query string, but not `''` — an empty-string
// value would otherwise serialize as e.g. `lookupId=` and could get rejected by the server, so
// empty strings are dropped here alongside `undefined`.
function omitEmptyStringParams(params: DeliveryEstimateQueryParams): DeliveryEstimateQueryParams {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== '')) as DeliveryEstimateQueryParams;
}

async function getFromPromiseExplainer<T>(baseUrl: string, id: string, suffix = ''): Promise<T> {
  try {
    const { data } = await GatewayAxiosClient.get<T>(`${baseUrl}/${encodeURIComponent(id)}${suffix}`);

    return data;
  } catch (error) {
    throw toDeliveryEstimatesError(error);
  }
}

export const deliveryEstimates = {
  list: {
    queryKey: 'delivery-experience-service/delivery-estimates',
    configurationType: 'no-cache' as const,
    fn: async (params: DeliveryEstimateQueryParams = {}): Promise<DeliveryEstimatePage> => {
      try {
        const { data, headers } = await GatewayAxiosClient.get<DeliveryEstimateSummary[]>(DELIVERY_ESTIMATES_BASE, {
          params: omitEmptyStringParams(params),
          // Serialize array filters (destinationLocation, shipOption, origin) as repeated params —
          // `destinationLocation=WA&destinationLocation=OR` — which the backend binds to a List.
          // axios 0.21's default array format uses `key[]=` brackets, which Spring does not bind.
          paramsSerializer: (serialized) => qs.stringify(serialized, { arrayFormat: 'repeat' }),
        });

        // Read both pagination models; the server populates exactly one per its X-Sampled flag.
        // For the unsampled path an absent X-Page-Next/X-Page-Previous means there is no next/previous
        // page (never inferred from rows.length); the sampled path instead reports its position via
        // X-Page-Number/X-Total-Pages and the exact distinct-signature count via X-Total-Count.
        return {
          rows: data,
          nextAnchor: headers['x-page-next'],
          prevAnchor: headers['x-page-previous'],
          pageNumber: toOptionalNumber(headers['x-page-number']),
          totalPages: toOptionalNumber(headers['x-total-pages']),
          totalCount: toOptionalNumber(headers['x-total-count']),
          sampled: headers['x-sampled'] === 'true',
          asOfDateTime: headers['x-as-of-date-time'],
        };
      } catch (error) {
        throw toDeliveryEstimatesError(error);
      }
    },
  },
  detail: {
    queryKey: 'delivery-experience-service/delivery-estimates/explanation',
    // A past promise's explanation is immutable, so a brief cache avoids refetching every time the
    // user clicks back to a row they already viewed (the list is `no-cache` because its contents move).
    configurationType: 'short-cache' as const,
    fn: (deliveryEstimateId: string): Promise<PromiseExplanation> =>
      getFromPromiseExplainer<PromiseExplanation>(DELIVERY_ESTIMATES_BASE, deliveryEstimateId),
  },
  failure: {
    queryKey: 'delivery-experience-service/delivery-estimates/failure',
    // A recorded failure never changes, so it shares the explanation's brief cache.
    configurationType: 'short-cache' as const,
    fn: (deliveryEstimateFailureId: string): Promise<DeliveryEstimateFailure> =>
      getFromPromiseExplainer<DeliveryEstimateFailure>(DELIVERY_ESTIMATE_FAILURES_BASE, deliveryEstimateFailureId),
  },
  estimatePayloads: {
    queryKey: 'delivery-experience-service/delivery-estimates/payloads',
    // A recorded call never changes, so it shares the explanation's brief cache.
    configurationType: 'short-cache' as const,
    fn: (deliveryEstimateId: string): Promise<CallPayloads> =>
      getFromPromiseExplainer<CallPayloads>(DELIVERY_ESTIMATES_BASE, deliveryEstimateId, '/payloads'),
  },
  failurePayloads: {
    queryKey: 'delivery-experience-service/delivery-estimate-failures/payloads',
    // A recorded call never changes, so it shares the explanation's brief cache.
    configurationType: 'short-cache' as const,
    fn: (deliveryEstimateFailureId: string): Promise<CallPayloads> =>
      getFromPromiseExplainer<CallPayloads>(DELIVERY_ESTIMATE_FAILURES_BASE, deliveryEstimateFailureId, '/payloads'),
  },
};
